import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@rapider/authentication';
import { ProjectDataService } from '@rapider/project-data';

/**
 * Guard to ensure an active project is set before accessing authenticated routes.
 * Follows these rules:
 * 1. If no activeProjectId in state, check localStorage and set if found.
 * 2. If user has only one project, set it as activeProjectId.
 * 3. If a valid token with projectId exists, set it as activeProjectId.
 * 4. If all fail, redirect to workspace selection.
 */
export const activeProjectGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const projectDataService = inject(ProjectDataService);

  // CRITICAL FIX: Prevent infinite redirect loops! 
  // If the user is already heading to a workspace selection, creation, or onboarding route, 
  // we must allow them to pass so they can resolve their missing active project status.
  if (state.url.includes('/select-workspace') || state.url.includes('/create-workspace') || state.url.includes('/onboarding')) {
    return true;
  }

  // 1. Check state and localStorage
  let activeProject = authStore.activeProject?.();
  let activeProjectId = activeProject?.id || localStorage.getItem('activeProjectId');

  if (activeProjectId) {
    // If not in state, but in localStorage, try to set it in state
    if (!activeProject) {
      // Optionally, trigger AuthStore.resolveWorkspaceFlow to hydrate state
      await new Promise((resolve) => {
        authStore.resolveWorkspaceFlow({});
        // Give the signal store a tick to update
        setTimeout(resolve, 100);
      });
    }
    return true;
  }

  // 2. Try to fetch all projects for the user
  // Try to get projects from AuthStore (if hydrated)
  const projects = authStore.projects?.() || [];
  if (projects.length === 1) {
    const project = projects[0];
    localStorage.setItem('activeProjectId', project.id);
    authStore.setActiveWorkspace({ project });
    return true;
  }

  // 3. Try to get from valid token
  try {
    const tokenData = await projectDataService.getProjectApiTokenData().toPromise();
    if (tokenData?.projectId) {
      localStorage.setItem('activeProjectId', tokenData.projectId);
      // Optionally, trigger AuthStore.resolveWorkspaceFlow to hydrate state
      await new Promise((resolve) => {
        authStore.resolveWorkspaceFlow({});
        setTimeout(resolve, 100);
      });
      return true;
    }
  } catch { }

  // 4. All failed, redirect
  router.navigate(['/select-workspace']);
  return false;
};
